import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLearnerSlideModal } from '../../hooks/useLearnerSlideModal';
import { LEARNER_SETTINGS_MODAL_CHROME_CLASS } from '../settings/learnerSettingsSectionThemes';

export interface ProfileSlideFormModalShellProps {
  open: boolean;
  onClose: () => void;
  titleId: string;
  title: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  /** e.g. `sm:max-w-3xl` — defaults to quick-settings width. */
  panelMaxWidthClass?: string;
  closeAriaLabel?: string;
}

/**
 * Profile edit / password modals — slide open/close like journey modals,
 * header/footer chrome matches quick settings (without tour help).
 */
export const ProfileSlideFormModalShell: React.FC<ProfileSlideFormModalShellProps> = ({
  open,
  onClose,
  titleId,
  title,
  children,
  footer,
  panelMaxWidthClass = 'sm:max-w-2xl',
  closeAriaLabel,
}) => {
  const { t: tCommon } = useTranslation('common');
  const { mounted, visible, panelRef, requestClose, handleExitTransitionEnd } = useLearnerSlideModal(
    open,
    onClose,
    undefined,
    { lockScroll: false }
  );

  if (!mounted) return null;

  return (
    <div className="learner-slide-modal-root fixed inset-0 z-[200] py-6 sm:py-10" role="presentation">
      <div
        className={`keyboard-slide-modal-backdrop fixed inset-0 bg-black/45 backdrop-blur-[2px] dark:bg-black/55${
          visible ? ' is-visible' : ''
        }`}
        aria-hidden="true"
        onClick={requestClose}
      />
      <div className="flex min-h-full items-end justify-center px-3 sm:items-center sm:px-6">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className={`keyboard-slide-modal-panel keyboard-slide-modal-panel--from-banner pointer-events-auto relative flex h-[min(85vh,780px)] w-[min(100%,26rem)] max-w-lg flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100 shadow-2xl dark:border-gray-600 dark:from-gray-900 dark:to-gray-950 sm:w-full sm:rounded-2xl ${panelMaxWidthClass}${
            visible ? ' is-visible' : ''
          }`}
          onTransitionEnd={handleExitTransitionEnd}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`flex shrink-0 items-center justify-between gap-2 border-b px-3 py-1.5 sm:px-4 ${LEARNER_SETTINGS_MODAL_CHROME_CLASS}`}
          >
            <h2 id={titleId} className="text-base font-semibold text-slate-900 dark:text-white">
              {title}
            </h2>
            <button
              type="button"
              onClick={requestClose}
              className="rounded-lg p-1 text-slate-600 transition-colors hover:bg-slate-200/80 dark:text-slate-300 dark:hover:bg-gray-700"
              aria-label={closeAriaLabel ?? tCommon('close')}
              title={tCommon('close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain">{children}</div>

          <footer
            className={`flex w-full shrink-0 items-center justify-center gap-2.5 border-t px-4 py-3 sm:px-5 ${LEARNER_SETTINGS_MODAL_CHROME_CLASS}`}
          >
            {footer}
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ProfileSlideFormModalShell;
